/**
 * SITE FURNISHINGS (2026-10-06, Matt: "I need a new trampoline family object in AlderCAD
 * that will go under the site ribbon"): a backyard trampoline with its safety enclosure —
 * ROUND (`trampoline`, diameter = min(width, depth)) or RECTANGULAR (`trampolineRect`,
 * Matt the same day: "I need a rectangular format trampoline, too" — the full width ×
 * depth, soft rounded corners like the real frames). Local frame (the object
 * convention): x across the width centred on 0, y from the anchor (y = 0) to the far
 * side, z up from the ground — the trampoline is centred at (0, d/2).
 *
 * Both shapes share ONE outline — a rounded rectangle whose corner radius is the whole
 * half-size for a round one — offset inward for each part, so the parts read the way a
 * real one does: U-legs (two posts and a ground bar each), the steel frame ring, a
 * padded spring cover, the dark jumping mat just inside it, and — when the height reaches
 * past the frame — enclosure poles with a padded top ring and the net between them on
 * the proto's translucent glass channel, so the mat stays visible through it. A height at
 * or under 4.5' builds the bare trampoline, no net.
 */
import type { MeshBuilder } from '../../extrude'
import type { Pt2 } from '@aj/bim-kernel'

/** the mat top above grade for a standard backyard trampoline */
export const TRAMPOLINE_FRAME_FT = 3
/** heights above this carry the safety enclosure */
const NET_MIN_HEIGHT_FT = 4.5
/** a rectangular frame's corner radius (the bent tube) */
const RECT_CORNER_FT = 0.75
/** half a U-leg's span along the frame */
const LEG_HALF_FT = 0.95
const TUBE = 0.07

export const TRAMPOLINE_KINDS = ['trampoline', 'trampolineRect'] as const

interface Shape {
  hx: number
  hy: number
  /** corner radius of the outer (spring-cover) outline */
  r: number
  cy: number
  round: boolean
  frameZ: number
  net: boolean
}

function shapeOf(kind: string, w: number, d: number, h: number, netOn = true): Shape {
  const round = kind !== 'trampolineRect'
  const hx = round ? Math.min(w, d) / 2 : w / 2
  const hy = round ? Math.min(w, d) / 2 : d / 2
  const tall = h > NET_MIN_HEIGHT_FT
  // the net switched off (Matt, 2026-10-06) leaves the bare trampoline at the standard height
  const net = tall && netOn
  return {
    hx, hy, cy: d / 2, round,
    r: round ? hx : Math.min(RECT_CORNER_FT, hx, hy),
    frameZ: tall ? TRAMPOLINE_FRAME_FT : Math.max(0.5, h),
    net,
  }
}

/** the outline inset by `inset` — a CCW rounded rectangle (a circle when round), with
 *  the same vertex count at every inset so two outlines pair vertex for vertex */
function outline(s: Shape, inset: number): Pt2[] {
  const hx = Math.max(0.2, s.hx - inset), hy = Math.max(0.2, s.hy - inset)
  const r = Math.max(0.01, Math.min(hx, hy, s.r - inset))
  const seg = s.round ? 16 : 6        // per quarter
  const pts: Pt2[] = []
  const corners: [number, number, number][] = [
    [hx - r, hy - r, 0], [-hx + r, hy - r, Math.PI / 2], [-hx + r, -hy + r, Math.PI], [hx - r, -hy + r, -Math.PI / 2],
  ]
  for (const [cx, cy, a0] of corners) {
    for (let i = 0; i <= seg; i++) {
      if (s.round && i === seg) continue      // a circle's quarters meet exactly
      const a = a0 + (Math.PI / 2) * (i / seg)
      pts.push([cx + Math.cos(a) * r, s.cy + cy + Math.sin(a) * r])
    }
  }
  return pts
}

/** cumulative arc length along a closed outline */
function arcs(pts: Pt2[]): number[] {
  const L = [0]
  for (let i = 1; i <= pts.length; i++) {
    const a = pts[i - 1], b = pts[i % pts.length]
    L.push(L[i - 1] + Math.hypot(b[0] - a[0], b[1] - a[1]))
  }
  return L
}

/** the point at arc length t along a closed outline */
function at(pts: Pt2[], L: number[], t: number): Pt2 {
  const total = L[L.length - 1]
  t = ((t % total) + total) % total
  for (let i = 1; i < L.length; i++) {
    if (t <= L[i] + 1e-9) {
      const a = pts[i - 1], b = pts[i % pts.length], k = (t - L[i - 1]) / Math.max(1e-9, L[i] - L[i - 1])
      return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k]
    }
  }
  return pts[0]
}

/** the arc length of the outline point nearest p */
function arcOf(pts: Pt2[], L: number[], p: Pt2): number {
  let best = Infinity, t = 0
  for (let i = 1; i <= pts.length; i++) {
    const a = pts[i - 1], b = pts[i % pts.length]
    const dx = b[0] - a[0], dy = b[1] - a[1], l2 = dx * dx + dy * dy || 1
    const k = Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2))
    const dd = Math.hypot(a[0] + dx * k - p[0], a[1] + dy * k - p[1])
    if (dd < best) { best = dd; t = L[i - 1] + k * Math.sqrt(l2) }
  }
  return t
}

/** each U-leg's two post positions on the frame line: four on a round one (at the
 *  diagonals); on a rectangular one two per long side, at its quarter points */
function legPosts(s: Shape, frame: Pt2[], L: number[]): [Pt2, Pt2][] {
  let centres: Pt2[]
  if (s.round) {
    centres = [45, 135, 225, 315].map((g) => {
      const a = (g * Math.PI) / 180
      return [Math.cos(a) * s.hx, s.cy + Math.sin(a) * s.hy] as Pt2
    })
  } else if (s.hy >= s.hx) {
    centres = [[s.hx, s.cy - s.hy / 2], [s.hx, s.cy + s.hy / 2], [-s.hx, s.cy + s.hy / 2], [-s.hx, s.cy - s.hy / 2]]
  } else {
    centres = [[s.hx / 2, s.cy + s.hy], [-s.hx / 2, s.cy + s.hy], [-s.hx / 2, s.cy - s.hy], [s.hx / 2, s.cy - s.hy]]
  }
  return centres.map((c) => {
    const t = arcOf(frame, L, c)
    return [at(frame, L, t - LEG_HALF_FT), at(frame, L, t + LEG_HALF_FT)]
  })
}

/** enclosure pole positions: eight evenly round a round one; on a rectangular one a pole
 *  at each corner and the rest spaced evenly (about 5') down each side */
function polePositions(s: Shape, net: Pt2[], L: number[], inset: number): Pt2[] {
  const total = L[L.length - 1]
  if (s.round) return Array.from({ length: 8 }, (_, i) => at(net, L, (total * (i + 0.5)) / 8))
  const hx = s.hx - inset, hy = s.hy - inset, r = Math.max(0.01, Math.min(hx, hy, s.r - inset))
  const k = r * (1 - Math.SQRT1_2)
  const out: Pt2[] = [[hx - k, s.cy + hy - k], [-hx + k, s.cy + hy - k], [-hx + k, s.cy - hy + k], [hx - k, s.cy - hy + k]]
  const along = (a: Pt2, b: Pt2) => {
    const n = Math.max(1, Math.round(Math.hypot(b[0] - a[0], b[1] - a[1]) / 5))
    for (let i = 1; i < n; i++) out.push([a[0] + ((b[0] - a[0]) * i) / n, a[1] + ((b[1] - a[1]) * i) / n])
  }
  along([hx, s.cy - hy], [hx, s.cy + hy]); along([-hx, s.cy - hy], [-hx, s.cy + hy])
  along([-hx, s.cy + hy], [hx, s.cy + hy]); along([-hx, s.cy - hy], [hx, s.cy - hy])
  return out
}

export function buildTrampoline(kind: string, w: number, d: number, h: number, b: MeshBuilder, glass: MeshBuilder, netOn = true): void {
  const s = shapeOf(kind, w, d, h, netOn)
  const { frameZ } = s

  /** vertical prism over a convex CCW polygon */
  const prism = (poly: Pt2[], zA: number, zB: number) => {
    const n = poly.length
    const bot = poly.map((q) => [q[0], q[1], zA])
    const top = poly.map((q) => [q[0], q[1], zB])
    for (let i = 2; i < n; i++) {
      b.addTri(top[0], top[i - 1], top[i], [0, 0, 1])
      b.addTri(bot[0], bot[i], bot[i - 1], [0, 0, -1])
    }
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      const nx = poly[j][1] - poly[i][1], ny = poly[i][0] - poly[j][0]
      const len = Math.hypot(nx, ny) || 1
      const nn = [nx / len, ny / len, 0]
      b.addTri(bot[i], bot[j], top[j], nn)
      b.addTri(bot[i], top[j], top[i], nn)
    }
  }
  /** the band between two paired outlines over [zA, zB], one convex quad per segment */
  const ring = (outer: Pt2[], inner: Pt2[], zA: number, zB: number) => {
    for (let i = 0; i < outer.length; i++) {
      const j = (i + 1) % outer.length
      prism([outer[i], outer[j], inner[j], inner[i]], zA, zB)
    }
  }
  const post = (p: Pt2, r: number, zA: number, zB: number) =>
    prism(Array.from({ length: 8 }, (_, i) => {
      const a = (Math.PI * 2 * i) / 8
      return [p[0] + Math.cos(a) * r, p[1] + Math.sin(a) * r] as Pt2
    }), zA, zB)
  const bar = (p: Pt2, q: Pt2, t: number, zA: number, zB: number) => {
    const dx = q[0] - p[0], dy = q[1] - p[1], L = Math.hypot(dx, dy) || 1
    const nx = (-dy / L) * t, ny = (dx / L) * t
    prism([[p[0] - nx, p[1] - ny], [q[0] - nx, q[1] - ny], [q[0] + nx, q[1] + ny], [p[0] + nx, p[1] + ny]], zA, zB)
  }

  const frame = outline(s, 0.2)
  const fL = arcs(frame)
  for (const [p, q] of legPosts(s, frame, fL)) {
    post(p, TUBE, 0, frameZ - 0.1)
    post(q, TUBE, 0, frameZ - 0.1)
    bar(p, q, TUBE, 0, TUBE * 2)
  }
  // the steel frame ring, the padded spring cover over it, the mat just inside
  const matInset = Math.max(0.9, Math.min(s.hx, s.hy) * 0.18)
  ring(outline(s, 0.2 - TUBE), outline(s, 0.2 + TUBE), frameZ - 0.24, frameZ - 0.1)
  const mat = outline(s, matInset)
  ring(outline(s, 0), mat, frameZ - 0.1, frameZ + 0.04)
  prism(mat, frameZ - 0.08, frameZ - 0.04)

  if (!s.net) return
  // the enclosure: poles rising from the frame, a padded top ring, the net between
  const net = outline(s, 0.28)
  const nL = arcs(net)
  for (const p of polePositions(s, net, nL, 0.28)) post(p, 0.08, frameZ - 0.24, h)
  ring(outline(s, 0.23), outline(s, 0.33), h - 0.12, h)
  const z0 = frameZ + 0.04, z1 = h - 0.12
  for (let i = 0; i < net.length; i++) {
    const p0 = net[i], p1 = net[(i + 1) % net.length]
    const ex = p1[0] - p0[0], ey = p1[1] - p0[1], len = Math.hypot(ex, ey) || 1
    const out = [ey / len, -ex / len, 0], inn = [-out[0], -out[1], 0]
    const A = [p0[0], p0[1], z0], B = [p1[0], p1[1], z0], C = [p1[0], p1[1], z1], D = [p0[0], p0[1], z1]
    // both faces, so the net reads from outside and from inside the enclosure
    glass.addTri(A, B, C, out); glass.addTri(A, C, D, out)
    glass.addTri(A, C, B, inn); glass.addTri(A, D, C, inn)
  }
}

/** the top-view trampoline: the spring-cover edge, the mat, the enclosure poles, and the
 *  U-legs dashed where they run hidden under the frame */
export function trampolinePlanSymbol(kind: string, w: number, d: number, h: number, netOn = true): { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] {
  const s = shapeOf(kind, w, d, h, netOn)
  const circle = (c: Pt2, r: number, segs: number): Pt2[] =>
    Array.from({ length: segs }, (_, i) => {
      const a = (Math.PI * 2 * i) / segs
      return [c[0] + Math.cos(a) * r, c[1] + Math.sin(a) * r] as Pt2
    })
  const out: { pts: Pt2[]; closed?: boolean; dashed?: boolean }[] = [
    { pts: outline(s, 0), closed: true },
    { pts: outline(s, Math.max(0.9, Math.min(s.hx, s.hy) * 0.18)), closed: true },
  ]
  const frame = outline(s, 0.2)
  for (const [p, q] of legPosts(s, frame, arcs(frame))) out.push({ pts: [p, q], dashed: true })
  if (s.net) {
    const net = outline(s, 0.28)
    for (const p of polePositions(s, net, arcs(net), 0.28)) out.push({ pts: circle(p, 0.1, 10), closed: true })
  }
  return out
}
